import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import {
  Footprints, Play, Square, RotateCcw, Target, Flame, MapPin, Clock,
  CheckCircle2, LogIn, Calendar,
  Pause, Gauge, Monitor, Smartphone, Activity, Trash2,
  Navigation, Mountain, Route, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { supabase } from "./supabase";
import type { Language } from "./data";
import { MusicPlayer } from "./MusicPlayer";
import { type MusicPlayerState } from "./useMusicPlayer";
import { useOrientation } from "./utils/useOrientation";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { StepCounter } from "./plugins/stepCounter";
import { ActivitySessionManager } from "./walk/ActivitySessionManager";
import { LocalActivityStorage, createLocalActivity } from "./walk/LocalActivityStorage";
import {
  syncActivity,
} from "./walk/ActivitySyncService";
import { checkLocationPermission, type GpsErrorType } from "./walk/LocationService";
import { speedToUnit, formatPace } from "./walk/Calculators";
import type { ActivityStats, GpsMode, GpsPoint } from "./walk/types";

const WalkMap = lazy(() => import("./walk/WalkMap").then(m => ({ default: m.WalkMap })));
import type { MapStyle } from "./walk/WalkMap";

// ─── Types ────────────────────────────────────────────────────────────────────

interface StepsTabProps {
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  lang: Language;
  weightKg?: number;
  userId?: string;
  isOnline?: boolean;
  onSignInRequest?: () => void;
  player: MusicPlayerState;
  isWalking?: boolean;
  onWalkingStateChange?: (isWalking: boolean) => void;
}

interface DayHistory {
  date: string;
  steps: number;
  goal: number;
}

interface WorkoutRecord {
  id: string;
  date: string;
  started_at: string;
  duration_secs: number;
  steps: number;
  distance_km: number;
  avg_speed_kmh: number;
  calories: number;
  tracking_mode: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_LENGTH_M = 0.762;
const DEFAULT_GOAL = 10000;
const DEFAULT_WEIGHT_KG = 70;
const MIN_STEP_INTERVAL_MS = 280;
const GRAVITY_ALPHA = 0.9;
const SMOOTHING = 0.5;
const ACCEL_THRESHOLD = 2.2;
const ACCEL_RESET = 0.9;
// ─── LocalStorage helpers (offline-first) ─────────────────────────────────────

interface LocalDay { steps: number; goal: number; activeSecs: number }
interface PendingRecord { date: string; steps: number; goal: number; activeSecs: number; userId?: string; deviceId: string }

const LS = {
  today: (date: string) => `steps_day_${date}`,
  history: "steps_history_v2",
  pending: "steps_pending_sync",

  saveDay(date: string, data: LocalDay) {
    try { localStorage.setItem(this.today(date), JSON.stringify(data)); } catch {}
  },
  loadDay(date: string): LocalDay | null {
    try { const r = localStorage.getItem(this.today(date)); return r ? JSON.parse(r) : null; } catch { return null; }
  },
  saveHistory(h: DayHistory[]) {
    try { localStorage.setItem(this.history, JSON.stringify(h)); } catch {}
  },
  loadHistory(): DayHistory[] {
    try { const r = localStorage.getItem(this.history); return r ? JSON.parse(r) : []; } catch { return []; }
  },
  queuePending(rec: PendingRecord) {
    try {
      const list: PendingRecord[] = JSON.parse(localStorage.getItem(this.pending) ?? "[]");
      const idx = list.findIndex(p => p.date === rec.date && p.userId === rec.userId && p.deviceId === rec.deviceId);
      if (idx >= 0) list[idx] = rec; else list.push(rec);
      localStorage.setItem(this.pending, JSON.stringify(list));
    } catch {}
  },
  clearPending(date: string, userId?: string, deviceId?: string) {
    try {
      const list: PendingRecord[] = JSON.parse(localStorage.getItem(this.pending) ?? "[]");
      localStorage.setItem(this.pending, JSON.stringify(list.filter(p => !(p.date === date && p.userId === userId && p.deviceId === deviceId))));
    } catch {}
  },
  getPending(): PendingRecord[] {
    try { return JSON.parse(localStorage.getItem(this.pending) ?? "[]"); } catch { return []; }
  },
};

function getDeviceId(): string {
  let id = localStorage.getItem("device_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("device_id", id); }
  return id;
}

function todayStr(): string {
  return new Date().toISOString().split("T")[0];
}

function calcCalories(steps: number, weightKg: number): number {
  return Math.round(steps * weightKg * 0.00035 * 100) / 100;
}

function calcDistanceKm(steps: number): number {
  return Math.round(steps * STEP_LENGTH_M / 10) / 100;
}

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function isDesktop(): boolean {
  return !("ontouchstart" in window) && navigator.maxTouchPoints === 0;
}

// ─── Weekly Activity Chart (Recharts) ─────────────────────────────────────────

function WeeklyActivityChart({ history, dir }: { history: DayHistory[]; dir: "rtl" | "ltr"; t: (k: string) => string }) {
  if (history.length === 0) return null;

  const sorted = [...history].sort((a, b) => b.date.localeCompare(a.date));
  const weekData = sorted.slice(0, 7).reverse().map(d => {
    const date = new Date(d.date + "T00:00:00");
    const dayLabelsAr: Record<number, string> = { 0: "أحد", 1: "إثنين", 2: "ثلاثاء", 3: "أربعاء", 4: "خميس", 5: "جمعة", 6: "سبت" };
    const dayLabelsEn: Record<number, string> = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };
    const labels = dir === "rtl" ? dayLabelsAr : dayLabelsEn;
    return {
      day: labels[date.getDay()] ?? d.date,
      steps: d.steps,
      distance: Math.round(d.steps * STEP_LENGTH_M / 10) / 100,
    };
  });

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 text-teal-500" />
        <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">
          {dir === "rtl" ? "النشاط الأسبوعي" : "Weekly Activity"}
        </h3>
      </div>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <BarChart data={weekData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "12px",
                fontSize: "12px",
                color: "#f1f5f9",
              }}
              labelStyle={{ color: "#94a3b8" }}
            />
            <Bar dataKey="steps" name={dir === "rtl" ? "خطوات" : "Steps"} fill="#14b8a6" radius={[6, 6, 0, 0]} barSize={20} />
            <Bar dataKey="distance" name={dir === "rtl" ? "كم" : "km"} fill="#22c55e" radius={[6, 6, 0, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-500" />
          <span className="text-stone-500 dark:text-slate-400">{dir === "rtl" ? "خطوات" : "Steps"}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-500" />
          <span className="text-stone-500 dark:text-slate-400">{dir === "rtl" ? "مسافة (كم)" : "Distance (km)"}</span>
        </span>
      </div>
    </div>
  );
}

// ─── Recent Workouts List ─────────────────────────────────────────────────────

function SwipeableWorkoutRow({ w, dir, onDelete }: {
  w: WorkoutRecord;
  dir: "rtl" | "ltr";
  t: (k: string) => string;
  onDelete: (id: string) => void;
}) {
  const [dragX, setDragX] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const startX = useRef(0);
  const dragging = useRef(false);
  const MAX_DRAG = 88;

  const modeLabel = () => (dir === "rtl" ? "حساس" : "Sensor");

  const d = new Date(w.started_at);
  const dateStr = d.toLocaleDateString(dir === "rtl" ? "ar-SA" : "en-US", { weekday: "short", month: "short", day: "numeric" });
  const timeStr = d.toLocaleTimeString(dir === "rtl" ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit" });

  const onPointerDown = (e: React.PointerEvent) => {
    startX.current = e.clientX;
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const delta = e.clientX - startX.current;
    const isRTL = dir === "rtl";
    const clamped = isRTL ? Math.max(0, Math.min(MAX_DRAG, delta)) : Math.min(0, Math.max(-MAX_DRAG, delta));
    setDragX(clamped);
  };
  const onPointerUp = () => {
    dragging.current = false;
    const threshold = MAX_DRAG * 0.6;
    const isRTL = dir === "rtl";
    const reached = isRTL ? dragX >= threshold : dragX <= -threshold;
    setDragX(reached ? (isRTL ? MAX_DRAG : -MAX_DRAG) : 0);
    if (reached) setConfirming(true);
  };

  const reveal = Math.min(MAX_DRAG, Math.abs(dragX));

  return (
    <div className="relative overflow-hidden">
      <div
        className="absolute inset-y-0 flex items-center justify-center bg-red-500 text-white font-bold text-sm transition-all"
        style={{ [dir === "rtl" ? "left" : "right"]: 0, width: `${reveal}px` }}
      >
        <Trash2 className="w-4 h-4" />
      </div>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative bg-white dark:bg-slate-800 px-4 py-3 flex items-center gap-3 transition-transform"
        style={{ transform: `translateX(${dragX}px)`, touchAction: "pan-y" }}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center shrink-0">
          <Footprints className="w-5 h-5 text-teal-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-bold text-stone-700 dark:text-slate-200">{dateStr}</span>
            <span className="text-[10px] text-stone-400 dark:text-slate-500">{timeStr}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-stone-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDuration(w.duration_secs)}
            </span>
            <span className="flex items-center gap-1">
              <Footprints className="w-3 h-3" />
              {w.steps.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {Number(w.distance_km).toFixed(2)} {dir === "rtl" ? "كم" : "km"}
            </span>
            <span className="flex items-center gap-1">
              <Smartphone className="w-3.5 h-3.5 text-teal-500" />
              {modeLabel()}
            </span>
          </div>
        </div>
        <div className="text-end shrink-0">
          <div className="flex items-center gap-1 text-xs font-bold text-orange-600 dark:text-orange-400">
            <Flame className="w-3 h-3" />
            {Math.round(Number(w.calories))}
          </div>
          <div className="text-[10px] text-stone-400 dark:text-slate-500">
            {Number(w.avg_speed_kmh).toFixed(1)} {dir === "rtl" ? "كم/س" : "km/h"}
          </div>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" onClick={() => setConfirming(false)}>
          <div className="max-w-sm w-full rounded-3xl bg-white dark:bg-slate-900 border border-stone-200 dark:border-white/15 p-6 text-center shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-red-500/20 flex items-center justify-center mb-4">
              <Trash2 className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-stone-800 dark:text-white font-bold text-lg mb-2">
              {dir === "rtl" ? "حذف التمرين؟" : "Delete workout?"}
            </h3>
            <p className="text-stone-500 dark:text-slate-300 text-sm mb-5 leading-relaxed">
              {dir === "rtl" ? "سيتم حذف هذا التمرين نهائياً." : "This workout will be permanently deleted."}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { onDelete(w.id); setConfirming(false); setDragX(0); }}
                className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors"
              >
                {dir === "rtl" ? "حذف" : "Delete"}
              </button>
              <button
                onClick={() => { setConfirming(false); setDragX(0); }}
                className="flex-1 py-3 rounded-xl bg-stone-100 dark:bg-white/10 hover:bg-stone-200 dark:hover:bg-white/15 text-stone-700 dark:text-white font-semibold text-sm transition-colors"
              >
                {dir === "rtl" ? "إلغاء" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecentWorkouts({ workouts, dir, t, onDelete }: { workouts: WorkoutRecord[]; dir: "rtl" | "ltr"; t: (k: string) => string; onDelete: (id: string) => void }) {
  if (!Array.isArray(workouts) || workouts.length === 0) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 dark:border-slate-700 flex items-center gap-2">
        <Activity className="w-4 h-4 text-teal-500" />
        <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">
          {dir === "rtl" ? "التمارين الأخيرة" : "Recent Workouts"}
        </h3>
        <span className="ms-auto text-[10px] text-stone-400 dark:text-slate-500">
          {dir === "rtl" ? "← اسحب للحذف" : "Swipe to delete →"}
        </span>
      </div>
      <div className="divide-y divide-stone-50 dark:divide-slate-700/50 max-h-72 overflow-y-auto scrollbar-hide">
        {workouts.map(w => (
          <SwipeableWorkoutRow
            key={w.id}
            w={w}
            dir={dir}
            t={t}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function StepsTab({ t, dir, weightKg, userId, isOnline = true, onSignInRequest, player, isWalking, onWalkingStateChange }: StepsTabProps) {
  const [steps, setSteps] = useState(0);
  const [goal, setGoal] = useState(DEFAULT_GOAL);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [, setSupported] = useState<boolean | null>(null);
  const [permissionNeeded, setPermissionNeeded] = useState(false);
  const [activeSecs, setActiveSecs] = useState(0);
  const [history, setHistory] = useState<DayHistory[]>([]);
  const [workouts, setWorkouts] = useState<WorkoutRecord[]>([]);
  const [goalInput, setGoalInput] = useState("");
  const [editingGoal, setEditingGoal] = useState(false);
  const [, setSavedToday] = useState(false);
  const [, setMotionWorking] = useState<boolean | null>(null);
  const [, setManualMode] = useState(false);
  const [desktopMode, setDesktopMode] = useState(false);

  // GPS tracking state
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsStats, setGpsStats] = useState<ActivityStats | null>(null);
  const [livePoints, setLivePoints] = useState<GpsPoint[]>([]);
  const [gpsMode] = useState<GpsMode>("high");
  const [mapStyle, setMapStyle] = useState<MapStyle>("day");
  const [gpsSearching, setGpsSearching] = useState(false);
  const [gpsError, setGpsError] = useState<GpsErrorType | null>(null);
  const [recenter, setRecenter] = useState(false);
  const sessionRef = useRef<ActivitySessionManager | null>(null);
  const gpsActiveRef = useRef(false);

  const orientation = useOrientation();
  const isLandscape = orientation === "landscape";

  // Refs for step detection
  const stepsRef = useRef(0);
  const lastStepTimeRef = useRef(0);
  const smoothedRef = useRef(0);
  const gravityRef = useRef({ x: 0, y: 0, z: 0 });
  const wasAboveRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const goalRef = useRef(DEFAULT_GOAL);
  const activeSecsRef = useRef(0);
  const [, setWakeLockActive] = useState(false);
  const wKg = weightKg ?? DEFAULT_WEIGHT_KG;

  const workoutStartRef = useRef<number>(0);
  const workoutStepsStartRef = useRef(0);
  const isPausedRef = useRef(false);
  const nativeStepListenerRef = useRef<PluginListenerHandle | null>(null);
  const usingNativeStepsRef = useRef(false);
  const [, setNativeStepActive] = useState(false);

  useEffect(() => { goalRef.current = goal; }, [goal]);
  useEffect(() => { activeSecsRef.current = activeSecs; }, [activeSecs]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  const INACTIVITY_PAUSE_MS = 5000;

  // ── Load today's data ──────────────────────────────────────────────────────
  useEffect(() => {
    loadToday();
    loadHistory();
    loadWorkouts();
  }, [userId]);

  useEffect(() => {
    if (isOnline) syncPending();
  }, [isOnline, userId]);

  // Detect desktop on mount
  useEffect(() => {
    if (isDesktop()) setDesktopMode(true);
  }, []);

  const loadToday = async () => {
    const local = LS.loadDay(todayStr());
    if (local) {
      setSteps(local.steps); stepsRef.current = local.steps;
      setGoal(local.goal); setActiveSecs(local.activeSecs);
    }
    if (!isOnline) return;
    try {
      let query = supabase.from("daily_steps").select("steps, goal, active_secs").eq("date", todayStr());
      if (userId) query = query.eq("user_id", userId);
      else query = query.eq("device_id", getDeviceId());
      const { data } = await query.maybeSingle();
      if (data) {
        setSteps(data.steps ?? 0); stepsRef.current = data.steps ?? 0;
        setGoal(data.goal ?? DEFAULT_GOAL); setActiveSecs(data.active_secs ?? 0);
        LS.saveDay(todayStr(), { steps: data.steps ?? 0, goal: data.goal ?? DEFAULT_GOAL, activeSecs: data.active_secs ?? 0 });
      }
    } catch {}
  };

  const loadHistory = async () => {
    const local = LS.loadHistory();
    if (local.length > 0) setHistory(local);
    if (!isOnline) return;
    try {
      const since = new Date(); since.setDate(since.getDate() - 29);
      let query = supabase.from("daily_steps").select("date, steps, goal")
        .gte("date", since.toISOString().split("T")[0]).order("date", { ascending: true });
      if (userId) query = query.eq("user_id", userId);
      else query = query.eq("device_id", getDeviceId());
      const { data } = await query;
      if (data) {
        const map = new Map(data.map(d => [d.date, d]));
        const filled: DayHistory[] = [];
        for (let i = 29; i >= 0; i--) {
          const d = new Date(); d.setDate(d.getDate() - i);
          const key = d.toISOString().split("T")[0];
          const entry = map.get(key);
          if (entry && entry.steps > 0) filled.push(entry);
          else if (i === 0) filled.push({ date: key, steps: 0, goal: DEFAULT_GOAL });
        }
        setHistory(filled);
        LS.saveHistory(filled);
      }
    } catch {}
  };

  const loadWorkouts = async () => {
    if (!isOnline) return;
    try {
      let query = supabase.from("workouts").select("*")
        .order("started_at", { ascending: false }).limit(20);
      if (userId) query = query.eq("user_id", userId);
      else query = query.eq("device_id", getDeviceId());
      const { data } = await query;
      if (Array.isArray(data)) setWorkouts(data as WorkoutRecord[]);
    } catch {}
  };

  const handleDeleteWorkout = async (workoutId: string) => {
    try {
      await supabase.from("workouts").delete().eq("id", workoutId);
      setWorkouts(prev => prev.filter(w => w.id !== workoutId));
    } catch {}
  };

  // ── Save to localStorage + Supabase ───────────────────────────────────────
  const saveToDb = useCallback(async (currentSteps: number, currentActiveSecs: number, currentGoal: number) => {
    const today = todayStr();
    const calories = calcCalories(currentSteps, wKg);
    const distance = calcDistanceKm(currentSteps);
    const deviceId = getDeviceId();

    LS.saveDay(today, { steps: currentSteps, goal: currentGoal, activeSecs: currentActiveSecs });
    setSavedToday(true);

    if (!isOnline) {
      LS.queuePending({ date: today, steps: currentSteps, goal: currentGoal, activeSecs: currentActiveSecs, userId, deviceId });
      await loadHistory();
      return;
    }

    try {
      if (userId) {
        const { data: existing } = await supabase.from("daily_steps").select("id").eq("user_id", userId).eq("date", today).maybeSingle();
        if (existing) {
          await supabase.from("daily_steps").update({ steps: currentSteps, goal: currentGoal, calories, distance_km: distance, active_secs: currentActiveSecs, updated_at: new Date().toISOString() }).eq("id", existing.id);
        } else {
          await supabase.from("daily_steps").insert({ user_id: userId, date: today, steps: currentSteps, goal: currentGoal, calories, distance_km: distance, active_secs: currentActiveSecs });
        }
      } else {
        await supabase.from("daily_steps").upsert({ device_id: deviceId, date: today, steps: currentSteps, goal: currentGoal, calories, distance_km: distance, active_secs: currentActiveSecs, updated_at: new Date().toISOString() }, { onConflict: "device_id,date" });
      }
      LS.clearPending(today, userId, deviceId);
    } catch {
      LS.queuePending({ date: today, steps: currentSteps, goal: currentGoal, activeSecs: currentActiveSecs, userId, deviceId });
    }
    await loadHistory();
  }, [wKg, userId, isOnline]);

  const syncPending = useCallback(async () => {
    const pending = LS.getPending();
    if (pending.length === 0) return;
    for (const rec of pending) {
      try {
        const calories = calcCalories(rec.steps, wKg);
        const distance = calcDistanceKm(rec.steps);
        if (rec.userId) {
          const { data: existing } = await supabase.from("daily_steps").select("id").eq("user_id", rec.userId).eq("date", rec.date).maybeSingle();
          if (existing) {
            await supabase.from("daily_steps").update({ steps: rec.steps, goal: rec.goal, calories, distance_km: distance, active_secs: rec.activeSecs, updated_at: new Date().toISOString() }).eq("id", existing.id);
          } else {
            await supabase.from("daily_steps").insert({ user_id: rec.userId, date: rec.date, steps: rec.steps, goal: rec.goal, calories, distance_km: distance, active_secs: rec.activeSecs });
          }
        } else {
          await supabase.from("daily_steps").upsert({ device_id: rec.deviceId, date: rec.date, steps: rec.steps, goal: rec.goal, calories, distance_km: distance, active_secs: rec.activeSecs, updated_at: new Date().toISOString() }, { onConflict: "device_id,date" });
        }
        LS.clearPending(rec.date, rec.userId, rec.deviceId);
      } catch {}
    }
    await loadHistory();
  }, [wKg, userId]);

  // ── Save workout to Supabase ──────────────────────────────────────────────
  const saveWorkout = useCallback(async (
    workoutSteps: number,
    workoutDuration: number,
    workoutDistance: number,
    workoutAvgSpeed: number,
    workoutCalories: number,
    mode: string,
  ) => {
    if (workoutSteps === 0 && workoutDuration === 0) return;
    const deviceId = getDeviceId();
    const startedAt = new Date(workoutStartRef.current).toISOString();

    if (!isOnline) return;
    try {
      const payload = {
        device_id: userId ? null : deviceId,
        user_id: userId ?? null,
        date: todayStr(),
        started_at: startedAt,
        ended_at: new Date().toISOString(),
        duration_secs: workoutDuration,
        steps: workoutSteps,
        distance_km: Math.round(workoutDistance * 1000) / 1000,
        avg_speed_kmh: Math.round(workoutAvgSpeed * 100) / 100,
        calories: Math.round(workoutCalories * 100) / 100,
        tracking_mode: mode,
      };
      await supabase.from("workouts").insert(payload);
      await loadWorkouts();
    } catch {}
  }, [userId, isOnline]);

  // ── Active time timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isRunning && !isPaused) {
      timerRef.current = setInterval(() => {
        if (lastStepTimeRef.current > 0 && Date.now() - lastStepTimeRef.current < INACTIVITY_PAUSE_MS) {
          setActiveSecs(s => s + 1);
        }
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRunning, isPaused]);

  // ── Auto-save every 10s while running ──────────────────────────────────────
  useEffect(() => {
    if (isRunning && !isPaused) {
      saveTimerRef.current = setInterval(() => {
        saveToDb(stepsRef.current, activeSecsRef.current, goal);
      }, 10000);
    } else {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    }
    return () => { if (saveTimerRef.current) clearInterval(saveTimerRef.current); };
  }, [isRunning, isPaused, goal, saveToDb]);

  const motionCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Wake Lock + Silent AudioContext ─────────────────────────────────────────
  const startNoSleep = useCallback(() => {
    try {
      if (audioCtxRef.current) return;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      audioCtxRef.current = ctx;
      oscillatorRef.current = osc;
    } catch {}
  }, []);

  const stopNoSleep = useCallback(() => {
    try {
      oscillatorRef.current?.stop();
      audioCtxRef.current?.close();
    } catch {}
    oscillatorRef.current = null;
    audioCtxRef.current = null;
  }, []);

  const requestWakeLock = useCallback(async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await (navigator as Navigator & { wakeLock: { request: (t: string) => Promise<WakeLockSentinel> } }).wakeLock.request("screen");
        setWakeLockActive(true);
        wakeLockRef.current.addEventListener("release", () => setWakeLockActive(false));
      }
    } catch {}
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
      setWakeLockActive(false);
    }
    stopNoSleep();
  }, [stopNoSleep]);


  // ── Step activity timestamp ─────────────────────────────────────────────────
  const bumpActivity = useCallback(() => {
    lastStepTimeRef.current = Date.now();
  }, []);

  // ── Step detection via DeviceMotion ───────────────────────────────────────
  const handleMotion = useCallback((e: DeviceMotionEvent) => {
    if (isPausedRef.current) return;

    let motionMag: number;
    const pure = e.acceleration;
    const withG = e.accelerationIncludingGravity;

    if (pure && (pure.x !== null || pure.y !== null || pure.z !== null)) {
      const x = pure.x ?? 0, y = pure.y ?? 0, z = pure.z ?? 0;
      motionMag = Math.sqrt(x * x + y * y + z * z);
    } else if (withG && (withG.x !== null || withG.y !== null || withG.z !== null)) {
      const x = withG.x ?? 0, y = withG.y ?? 0, z = withG.z ?? 0;
      gravityRef.current.x = GRAVITY_ALPHA * gravityRef.current.x + (1 - GRAVITY_ALPHA) * x;
      gravityRef.current.y = GRAVITY_ALPHA * gravityRef.current.y + (1 - GRAVITY_ALPHA) * y;
      gravityRef.current.z = GRAVITY_ALPHA * gravityRef.current.z + (1 - GRAVITY_ALPHA) * z;
      const mx = x - gravityRef.current.x;
      const my = y - gravityRef.current.y;
      const mz = z - gravityRef.current.z;
      motionMag = Math.sqrt(mx * mx + my * my + mz * mz);
    } else {
      return;
    }

    setMotionWorking(prev => { if (prev !== true) return true; return prev; });

    smoothedRef.current = SMOOTHING * smoothedRef.current + (1 - SMOOTHING) * motionMag;
    const val = smoothedRef.current;

    const now = Date.now();
    if (wasAboveRef.current && now - lastStepTimeRef.current > MIN_STEP_INTERVAL_MS) {
      wasAboveRef.current = false;
    }

    if (!wasAboveRef.current && val > ACCEL_THRESHOLD) {
      wasAboveRef.current = true;
      if (now - lastStepTimeRef.current > MIN_STEP_INTERVAL_MS) {
        lastStepTimeRef.current = now;
        stepsRef.current += 1;
        setSteps(stepsRef.current);
        LS.saveDay(todayStr(), { steps: stepsRef.current, goal: goalRef.current, activeSecs: activeSecsRef.current });
        bumpActivity();
      }
    } else if (wasAboveRef.current && val < ACCEL_RESET) {
      wasAboveRef.current = false;
    }
  }, []);

  const startCounting = useCallback(async () => {
    setMotionWorking(null);
    smoothedRef.current = 0;
    gravityRef.current = { x: 0, y: 0, z: 0 };
    wasAboveRef.current = false;
    workoutStartRef.current = Date.now();
    workoutStepsStartRef.current = stepsRef.current;

    // Try native hardware step counter first (works with screen off / phone in pocket)
    if (Capacitor.isNativePlatform()) {
      try {
        const avail = await StepCounter.isAvailable();
        if (avail.available) {
          if (!avail.hasPermission) {
            const perm = await StepCounter.requestPermission();
            if (!perm.granted) throw new Error("no permission");
          }
          await StepCounter.start();
          usingNativeStepsRef.current = true;
          setNativeStepActive(true);
          const listener = await StepCounter.addListener("stepUpdate", (data) => {
            if (isPausedRef.current) return;
            const workoutSteps = data.steps;
            const totalSteps = workoutStepsStartRef.current + workoutSteps;
            stepsRef.current = totalSteps;
            setSteps(totalSteps);
            LS.saveDay(todayStr(), { steps: totalSteps, goal: goalRef.current, activeSecs: activeSecsRef.current });
            bumpActivity();
          });
          nativeStepListenerRef.current = listener;
          setMotionWorking(true);

          setIsRunning(true);
          setIsPaused(false);
          setPermissionNeeded(false);
          startNoSleep();
          await requestWakeLock();
          return;
        }
      } catch {
        // Fall through to accelerometer fallback
      }
    }

    // Fallback: browser accelerometer (DeviceMotionEvent)
    usingNativeStepsRef.current = false;
    setNativeStepActive(false);

    // iOS 13+ needs permission for DeviceMotion
    if (typeof (DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === "function") {
      const perm = await (DeviceMotionEvent as unknown as { requestPermission: () => Promise<string> }).requestPermission();
      if (perm !== "granted") return;
    }

    window.addEventListener("devicemotion", handleMotion);

    setIsRunning(true);
    setIsPaused(false);
    setPermissionNeeded(false);
    startNoSleep();
    await requestWakeLock();

    if (motionCheckRef.current) clearTimeout(motionCheckRef.current);
    motionCheckRef.current = setTimeout(() => {
      setMotionWorking(prev => {
        if (prev !== true && !isDesktop()) { setManualMode(true); return false; }
        return prev;
      });
    }, 2500);
  }, [handleMotion, startNoSleep, requestWakeLock]);

  const stopCounting = useCallback(() => {
    window.removeEventListener("devicemotion", handleMotion);
    if (motionCheckRef.current) clearTimeout(motionCheckRef.current);

    // Stop native step counter if it was running
    if (usingNativeStepsRef.current) {
      StepCounter.stop().catch(() => {});
      nativeStepListenerRef.current?.remove().catch(() => {});
      nativeStepListenerRef.current = null;
      usingNativeStepsRef.current = false;
      setNativeStepActive(false);
    }

    setIsRunning(false);
    setIsPaused(false);
    releaseWakeLock();
    saveToDb(stepsRef.current, activeSecsRef.current, goal);

    // Save workout session
    const workoutSteps = stepsRef.current - workoutStepsStartRef.current;
    const workoutDuration = activeSecsRef.current;
    const workoutDistance = calcDistanceKm(workoutSteps);
    const workoutAvgSpeed = workoutDuration > 0 ? (workoutDistance / (workoutDuration / 3600)) : 0;
    const workoutCalories = calcCalories(workoutSteps, wKg);

    saveWorkout(workoutSteps, workoutDuration, workoutDistance, workoutAvgSpeed, workoutCalories, "accelerometer");
  }, [handleMotion, activeSecs, goal, saveToDb, releaseWakeLock, wKg, saveWorkout]);

  const pauseCounting = useCallback(() => {
    setIsPaused(true);
    isPausedRef.current = true;
  }, []);

  const resumeCounting = useCallback(() => {
    setIsPaused(false);
    isPausedRef.current = false;
    lastStepTimeRef.current = Date.now(); // reset to avoid inactivity gap
    bumpActivity();
  }, [bumpActivity]);


  // ── Desktop simulation ─────────────────────────────────────────────────────
  const simulateStep = useCallback(() => {
    if (isPausedRef.current) return;
    lastStepTimeRef.current = Date.now();
    stepsRef.current += 1 + Math.floor(Math.random() * 3); // 1-3 steps per tap
    setSteps(stepsRef.current);
    LS.saveDay(todayStr(), { steps: stepsRef.current, goal: goalRef.current, activeSecs: activeSecsRef.current });
    bumpActivity();
  }, [bumpActivity]);

  const handleStartStop = async () => {
    if (isRunning) {
      stopCounting();
      // Stop GPS session too
      if (sessionRef.current) {
        const { stats: finalStats, points } = sessionRef.current.finish();
        setGpsStats(finalStats);
        setLivePoints(points);
        sessionRef.current = null;
        setGpsActive(false);
        gpsActiveRef.current = false;
        onWalkingStateChange?.(false);
        // Save activity if we got GPS data
        if (finalStats.gpsPointCount > 0) {
          const localActivity = createLocalActivity(finalStats, points);
          LocalActivityStorage.save(localActivity);
          if (isOnline) {
            syncActivity(localActivity, userId).then((r) => {
              if (!r.success) {
                LocalActivityStorage.save(localActivity);
              }
            }).catch(() => {
              LocalActivityStorage.save(localActivity);
            });
          }
        }
      }
    } else {
      // Start GPS tracking alongside step counting
      setGpsError(null);
      setGpsSearching(true);
      const permResult = await checkLocationPermission();
      if (!permResult.granted && permResult.error === "permission-denied") {
        setGpsSearching(false);
        setGpsError(permResult.error);
      } else {
        if (!sessionRef.current) sessionRef.current = new ActivitySessionManager();
        const session = sessionRef.current;
        session.onStart((s) => {
          setGpsStats(s);
        }, (pts) => {
          setLivePoints(pts);
        });
        const ok = await session.start(weightKg ?? 70, gpsMode);
        if (ok) {
          setGpsActive(true);
          gpsActiveRef.current = true;
          setRecenter(true);
          setTimeout(() => setRecenter(false), 1000);
          onWalkingStateChange?.(true);
        }
      }

      if (desktopMode) {
        // Desktop: skip motion permission, go straight to simulation
        setSupported(true);
        setMotionWorking(false);
        setManualMode(true);
        workoutStartRef.current = Date.now();
        workoutStepsStartRef.current = stepsRef.current;
        setIsRunning(true);
        setIsPaused(false);
        startNoSleep();
        await requestWakeLock();
        return;
      }
      if (typeof DeviceMotionEvent === "undefined") {
        setSupported(false);
        return;
      }
      if (typeof (DeviceMotionEvent as unknown as { requestPermission?: unknown }).requestPermission === "function") {
        setPermissionNeeded(true);
      } else {
        setSupported(true);
        await startCounting();
      }
    }
  };

  const handlePermissionClick = async () => {
    setSupported(true);
    await startCounting();
  };

  const handleReset = () => {
    if (isRunning) stopCounting();
    if (sessionRef.current) {
      sessionRef.current.stop();
      sessionRef.current = null;
      setGpsActive(false);
      gpsActiveRef.current = false;
      onWalkingStateChange?.(false);
    }
    setGpsStats(null);
    setLivePoints([]);
    stepsRef.current = 0;
    setSteps(0);
    setActiveSecs(0);
    setSavedToday(false);
    saveToDb(0, 0, goal);
  };

  const handleSetGoal = () => {
    const n = parseInt(goalInput);
    if (n > 0) {
      setGoal(n);
      saveToDb(stepsRef.current, activeSecs, n);
    }
    setEditingGoal(false);
    setGoalInput("");
  };

  // ── Handle screen lock / app background ──────────────────────────────────
  const hiddenAtRef = useRef<number>(0);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else if (isRunning && !isPaused && hiddenAtRef.current > 0) {
        const wasWalking = hiddenAtRef.current - lastStepTimeRef.current < INACTIVITY_PAUSE_MS;
        if (wasWalking) {
          lastStepTimeRef.current = Date.now();
        }
        hiddenAtRef.current = 0;
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isRunning, isPaused, requestWakeLock]);

  // ── Pause step counting while a GPS walk is active (avoid double-counting) ──
  useEffect(() => {
    if (isWalking && isRunning && !isPaused) {
      pauseCounting();
    } else if (!isWalking && isRunning && isPaused) {
      resumeCounting();
    }
  }, [isWalking, isRunning, isPaused, pauseCounting, resumeCounting]);

  // ── Estimate steps from GPS distance while GPS walk is active ────────────────
  const gpsStepsBaseRef = useRef(0);
  useEffect(() => {
    if (gpsActive && gpsStats && gpsStats.distance > 0) {
      if (gpsStepsBaseRef.current === 0) gpsStepsBaseRef.current = stepsRef.current;
      const gpsSteps = Math.round(gpsStats.distance / STEP_LENGTH_M);
      const totalSteps = gpsStepsBaseRef.current + gpsSteps;
      stepsRef.current = totalSteps;
      setSteps(totalSteps);
      LS.saveDay(todayStr(), { steps: totalSteps, goal: goalRef.current, activeSecs: activeSecsRef.current });
    }
    if (!gpsActive) {
      gpsStepsBaseRef.current = 0;
    }
  }, [gpsActive, gpsStats]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener("devicemotion", handleMotion);
      if (usingNativeStepsRef.current) {
        StepCounter.stop().catch(() => {});
        nativeStepListenerRef.current?.remove().catch(() => {});
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
      if (motionCheckRef.current) clearTimeout(motionCheckRef.current);
      if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; }
      if (sessionRef.current) { sessionRef.current.stop(); sessionRef.current = null; }
      try { oscillatorRef.current?.stop(); audioCtxRef.current?.close(); } catch {}
    };
  }, [handleMotion]);

  const pct = steps / goal;
  const calories = calcCalories(steps, wKg);
  const distanceKm = calcDistanceKm(steps);
  const activeMin = Math.floor(activeSecs / 60);
  const activeSec = activeSecs % 60;

  return (
    <>
    <div className={isLandscape ? "max-w-5xl mx-auto pb-32 grid grid-cols-2 gap-4 items-start" : "space-y-5 max-w-lg mx-auto pb-32"} dir={dir}>

    {/* ── Left column (landscape) / top (portrait) ── */}
    <div className={isLandscape ? "space-y-4" : "contents"}>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-400 flex items-center justify-center shadow-md">
          <Footprints className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-black text-stone-800 dark:text-slate-100 text-lg leading-tight">{t("stepsTab")}</h2>
          <p className="text-xs text-stone-400 dark:text-slate-500">{t("helpStepsDesc")}</p>
        </div>
        {/* Tracking mode badge */}
        {isRunning && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[10px] font-bold bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400">
            <Smartphone className="w-3 h-3" />
            {dir === "rtl" ? "حساس" : "Sensor"}
          </div>
        )}
      </div>

      {/* ── Steps Hero: circular progress + organized stats ── */}
      <div className="relative overflow-hidden rounded-3xl p-5 border border-stone-100 dark:border-slate-700 shadow-sm"
        style={{ background: "linear-gradient(160deg, #ffffff 0%, #f0fdfa 60%, #ecfeff 100%)" }}>
        {/* soft decorative blobs */}
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-teal-200/30 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-8 w-36 h-36 rounded-full bg-cyan-200/20 blur-2xl pointer-events-none" />

        <div className="relative flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-1 py-2">
            <span className="font-black tabular-nums leading-none"
              style={{
                fontSize: steps >= 10000 ? 40 : steps >= 1000 ? 44 : 48,
                color: "#0d9488",
                textShadow: "0 2px 8px rgba(13,148,136,0.3)",
              }}>
              {steps.toLocaleString()}
            </span>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#0d9488" }}>
              {t("stepsUnit")}
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#0d9488" }} />
              <span className="text-[11px] font-medium" style={{ color: "#0d9488" }}>
                {Math.round(pct * 100)}% {t("stepsGoalLabel")} {goal.toLocaleString()}
              </span>
            </div>
          </div>

          {/* organized stats grid (hidden when GPS active to avoid duplication) */}
          {!gpsActive && (
          <div className="grid grid-cols-3 gap-2.5 w-full">
            <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-white/70 dark:bg-slate-800/60 backdrop-blur-sm border border-stone-100 dark:border-slate-700 px-2 py-3">
              <Flame className="w-4 h-4 text-orange-500 mb-0.5" />
              <span className="text-lg font-black tabular-nums text-stone-800 dark:text-slate-100 leading-none">{Math.round(calories)}</span>
              <span className="text-[10px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide">{dir === "rtl" ? "سعرة" : "kcal"}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-white/70 dark:bg-slate-800/60 backdrop-blur-sm border border-stone-100 dark:border-slate-700 px-2 py-3">
              <MapPin className="w-4 h-4 text-sky-500 mb-0.5" />
              <span className="text-lg font-black tabular-nums text-stone-800 dark:text-slate-100 leading-none">{distanceKm.toFixed(2)}</span>
              <span className="text-[10px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide">{dir === "rtl" ? "كم" : "km"}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-white/70 dark:bg-slate-800/60 backdrop-blur-sm border border-stone-100 dark:border-slate-700 px-2 py-3">
              <Clock className="w-4 h-4 text-teal-500 mb-0.5" />
              <span className="text-lg font-black tabular-nums text-stone-800 dark:text-slate-100 leading-none">{String(activeMin).padStart(2, "0")}:{String(activeSec).padStart(2, "0")}</span>
              <span className="text-[10px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide">{dir === "rtl" ? "وقت" : "time"}</span>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Auth sync banner */}
      {!userId && (
        <button
          onClick={onSignInRequest}
          className="w-full flex items-center gap-3 bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/30 rounded-2xl px-4 py-3 hover:bg-teal-100 dark:hover:bg-teal-500/20 transition-colors text-start"
        >
          <div className="w-8 h-8 rounded-xl bg-teal-500/20 flex items-center justify-center shrink-0">
            <LogIn className="w-4 h-4 text-teal-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-teal-700 dark:text-teal-300">{t("authSyncBanner")}</p>
            <p className="text-[10px] text-teal-500 dark:text-teal-400 mt-0.5">{t("signIn")} →</p>
          </div>
        </button>
      )}

      {/* Signed-in indicator */}
      {userId && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
          <p className="text-xs text-green-700 dark:text-green-400 font-medium">
            {dir === "rtl" ? "حسابك متصل — يتم حفظ الخطوات محلياً ومزامنتها عند توفر الاتصال" : "Account connected — steps are saved locally and synced when online"}
          </p>
        </div>
      )}

      {/* Desktop notice */}
      {desktopMode && (
        <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-2xl px-4 py-3">
          <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
            <Monitor className="w-4 h-4 text-blue-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-blue-700 dark:text-blue-400">
              {dir === "rtl" ? "وضع الكمبيوتر — لا توجد حساسات" : "Desktop Mode — No Sensors"}
            </p>
            <p className="text-[11px] text-blue-600/80 dark:text-blue-500/80 mt-0.5">
              {dir === "rtl"
                ? "استخدم زر المحاكاة لاختبار العداد"
                : "Use the simulation button to test the counter"}
            </p>
          </div>
        </div>
      )}

      </div>{/* end left column (landscape only) */}

      {/* Start / Pause / Stop buttons */}
      {!permissionNeeded && (
        <div className="flex gap-3 w-full">
          {!isRunning ? (
            <button
              onClick={handleStartStop}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg, #14b8a6, #0d9488)",
                boxShadow: "0 8px 24px -6px rgba(20,184,166,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                color: "white",
              }}
            >
              <Play className="w-5 h-5" />
              {dir === "rtl" ? "ابدأ التمرين" : "Start Workout"}
            </button>
          ) : (
            <>
              {!isPaused ? (
                <button
                  onClick={pauseCounting}
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base transition-all active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                    boxShadow: "0 8px 24px -6px rgba(245,158,11,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                    color: "white",
                  }}
                >
                  <Pause className="w-5 h-5" />
                  {dir === "rtl" ? "إيقاف مؤقت" : "Pause"}
                </button>
              ) : (
                <button
                  onClick={resumeCounting}
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base transition-all active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #14b8a6, #0d9488)",
                    boxShadow: "0 8px 24px -6px rgba(20,184,166,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                    color: "white",
                  }}
                >
                  <Play className="w-5 h-5" />
                  {dir === "rtl" ? "استئناف" : "Resume"}
                </button>
              )}
              <button
                onClick={handleStartStop}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #ef4444, #f87171)",
                  boxShadow: "0 8px 24px -6px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.2)",
                  color: "white",
                }}
              >
                <Square className="w-5 h-5" />
                {dir === "rtl" ? "إيقاف وحفظ" : "Stop & Save"}
              </button>
            </>
          )}
          <button
            onClick={handleReset}
            title={t("stepsReset")}
            aria-label={t("stepsReset")}
            className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all active:scale-95"
            style={{ background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)", boxShadow: "0 4px 12px -4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)", color: "#64748b" }}
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* iOS permission prompt */}
      {permissionNeeded && !isRunning && (
        <button
          onClick={handlePermissionClick}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-400 text-white font-bold text-base shadow-md hover:shadow-lg transition-all hover:scale-[1.02] active:scale-95"
        >
          {t("stepsPermissionBtn")} — {t("stepsPermission")}
        </button>
      )}

      {/* Desktop simulation button */}
      {desktopMode && isRunning && (
        <button
          onPointerDown={simulateStep}
          className="w-full py-8 rounded-3xl bg-gradient-to-br from-blue-500 to-violet-500 text-white font-black text-xl shadow-lg active:scale-95 transition-transform select-none flex flex-col items-center gap-1"
          style={{ touchAction: "none" }}
        >
          <Activity className="w-7 h-7" />
          {dir === "rtl" ? "محاكاة المشي" : "Simulate Walking"}
        </button>
      )}

      {/* ══ GPS Map & Live Stats (shown when GPS is active) ══ */}
      {gpsActive && (
        <>
          {/* GPS searching indicator */}
          {gpsSearching && (
            <div className="bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 rounded-2xl p-3 flex items-center gap-3">
              <Navigation className="w-5 h-5 text-sky-500 animate-pulse shrink-0" />
              <p className="text-sm font-bold text-sky-700 dark:text-sky-400">
                {dir === "rtl" ? "جارٍ البحث عن إشارة GPS..." : "Searching for GPS signal..."}
              </p>
            </div>
          )}

          {/* GPS permission error */}
          {gpsError === "permission-denied" && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">
                  {dir === "rtl" ? "إذن الموقع مرفوض" : "Location permission denied"}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-500/80 mt-1">
                  {dir === "rtl"
                    ? "افتح إعدادات الهاتف ← التطبيقات ← اسمح بالوصول إلى الموقع"
                    : "Open phone Settings ← Apps ← Allow location access"}
                </p>
              </div>
              <button
                onClick={() => { setGpsError(null); }}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold active:scale-95 transition-transform"
              >
                {dir === "rtl" ? "إعادة" : "Retry"}
              </button>
            </div>
          )}

          {/* Live map */}
          <div className="relative w-full rounded-3xl overflow-hidden border border-stone-100 dark:border-slate-700 shadow-lg" style={{ height: 300 }}>
            <Suspense fallback={
              <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                <div className="text-stone-400 text-sm">{dir === "rtl" ? "جارٍ تحميل الخريطة..." : "Loading map..."}</div>
              </div>
            }>
              <WalkMap
                livePoints={livePoints}
                savedPoints={null}
                isTracking={gpsActive}
                recenter={recenter}
                dir={dir}
                mapStyle={mapStyle}
                onMapStyleChange={setMapStyle}
              />
            </Suspense>

            {/* GPS status badge */}
            {gpsStats && (
              <div className="absolute top-3 left-3 z-[500] flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
                <div className={`w-2 h-2 rounded-full ${
                  gpsStats.gpsPointCount === 0 ? "bg-stone-400"
                  : gpsStats.gpsAccuracy <= 10 ? "bg-green-500"
                  : gpsStats.gpsAccuracy <= 25 ? "bg-yellow-500" : "bg-red-500"
                }`} />
                <span className="text-white text-[10px] font-semibold">
                  {gpsStats.gpsPointCount === 0
                    ? (dir === "rtl" ? "بانتظار GPS" : "Waiting GPS")
                    : gpsStats.gpsAccuracy <= 10
                      ? (dir === "rtl" ? "دقة عالية" : "High")
                      : gpsStats.gpsAccuracy <= 25
                        ? (dir === "rtl" ? "دقة متوسطة" : "Moderate")
                        : (dir === "rtl" ? "دقة منخفضة" : "Poor")}
                </span>
                {gpsStats.gpsAccuracy > 0 && (
                  <span className="text-white/60 text-[10px]">±{Math.round(gpsStats.gpsAccuracy)}m</span>
                )}
              </div>
            )}

            {/* Recenter button */}
            <button
              onClick={() => setRecenter(true)}
              aria-label={dir === "rtl" ? "توسيط الخريطة" : "Recenter map"}
              className="absolute bottom-3 right-3 z-[500] w-10 h-10 rounded-full bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center active:scale-90 transition-transform"
            >
              <Navigation className="w-5 h-5 text-sky-500" />
            </button>
          </div>

          {/* GPS live stats grid (unified) */}
          {gpsStats && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl p-3 bg-sky-50 dark:bg-sky-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Route className="w-3.5 h-3.5 text-sky-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "المسافة" : "Distance"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-sky-500">{(gpsStats.distance / 1000).toFixed(2)}</span>
                  <span className="text-[10px] text-stone-400">km</span>
                </div>
              </div>
              <div className="rounded-2xl p-3 bg-orange-50 dark:bg-orange-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Flame className="w-3.5 h-3.5 text-orange-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "السعرات" : "Calories"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-orange-500">{Math.round(gpsStats.calories)}</span>
                  <span className="text-[10px] text-stone-400">kcal</span>
                </div>
              </div>
              <div className="rounded-2xl p-3 bg-violet-50 dark:bg-violet-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Gauge className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "السرعة" : "Speed"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-violet-500">{speedToUnit(gpsStats.currentSpeed, "km").toFixed(1)}</span>
                  <span className="text-[10px] text-stone-400">km/h</span>
                </div>
              </div>
              <div className="rounded-2xl p-3 bg-green-50 dark:bg-green-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Activity className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "الإيقاع" : "Pace"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-green-500">{gpsStats.avgPace > 0 ? formatPace(gpsStats.avgPace, "km") : "--:--"}</span>
                  <span className="text-[10px] text-stone-400">min/km</span>
                </div>
              </div>
              <div className="rounded-2xl p-3 bg-emerald-50 dark:bg-emerald-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Mountain className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "الارتفاع" : "Elevation"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-emerald-500">{gpsStats.currentElevation !== null ? Math.round(gpsStats.currentElevation) : "—"}</span>
                  <span className="text-[10px] text-stone-400">m</span>
                </div>
              </div>
              <div className="rounded-2xl p-3 bg-teal-50 dark:bg-teal-500/10 border border-stone-100 dark:border-slate-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock className="w-3.5 h-3.5 text-teal-500" />
                  <span className="text-[10px] font-semibold text-stone-500 dark:text-slate-400 uppercase">{dir === "rtl" ? "وقت الحركة" : "Moving time"}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black tabular-nums text-teal-500">{String(Math.floor(gpsStats.movingSecs / 60)).padStart(2, "0")}:{String(gpsStats.movingSecs % 60).padStart(2, "0")}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <MusicPlayer dir={dir} player={player} />

      {/* Goal setting */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-500" />
            <span className="text-sm font-bold text-stone-700 dark:text-slate-200">{t("stepsGoal")}</span>
          </div>
          <button onClick={() => { setEditingGoal(!editingGoal); setGoalInput(String(goal)); }} className="text-xs text-teal-500 hover:text-teal-600 font-semibold">
            {t("stepsGoalSet")}
          </button>
        </div>

        {editingGoal ? (
          <div className="flex gap-2">
            <input
              type="number"
              value={goalInput}
              onChange={e => setGoalInput(e.target.value)}
              className="flex-1 border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2 text-sm bg-white dark:bg-slate-700 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-400"
              placeholder="e.g. 10000"
            />
            <button onClick={handleSetGoal} className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-sm font-bold transition-colors">✓</button>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {[5000, 8000, 10000, 12000, 15000].map(g => (
              <button
                key={g}
                onClick={() => { setGoal(g); saveToDb(stepsRef.current, activeSecs, g); }}
                className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${
                  goal === g ? "bg-teal-500 text-white shadow" : "bg-stone-100 dark:bg-slate-700 text-stone-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-teal-500/20"
                }`}
              >
                {g.toLocaleString()}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Activity Chart (Recharts) */}
      <WeeklyActivityChart history={history} dir={dir} t={t} />

      {/* Recent Workouts */}
      <RecentWorkouts
        workouts={workouts}
        dir={dir}
        t={t}
        onDelete={handleDeleteWorkout}
      />

      {/* 30-day history */}
      {history.filter(d => d.steps > 0).length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 dark:border-slate-700 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-teal-500" />
            <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">
              {dir === "rtl" ? "سجل الخطوات" : "Steps Log"}
            </h3>
          </div>
          <div className="divide-y divide-stone-50 dark:divide-slate-700/50 max-h-72 overflow-y-auto scrollbar-hide">
            {[...history].filter(d => d.steps > 0).sort((a, b) => b.date.localeCompare(a.date)).map(day => {
              const d = new Date(day.date + "T00:00:00");
              const isToday = day.date === todayStr();
              const goalMet = day.steps >= day.goal;
              const dayPct = Math.min(100, Math.round((day.steps / day.goal) * 100));
              return (
                <div key={day.date} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 ${
                    isToday ? "bg-teal-500 text-white" : goalMet ? "bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300" : "bg-stone-100 dark:bg-slate-700 text-stone-500 dark:text-slate-400"
                  }`}>
                    {d.getDate()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-stone-600 dark:text-slate-300">
                        {isToday ? (dir === "rtl" ? "اليوم" : "Today") : d.toLocaleDateString(dir === "rtl" ? "ar-SA" : "en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </span>
                      <span className={`text-xs font-bold ${goalMet ? "text-green-600 dark:text-green-400" : "text-stone-600 dark:text-slate-300"}`}>
                        {day.steps.toLocaleString()} {goalMet && "✓"}
                      </span>
                    </div>
                    <div className="h-1.5 bg-stone-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${goalMet ? "bg-green-400" : "bg-teal-400"}`} style={{ width: `${dayPct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tip */}
      <div className="bg-teal-50 dark:bg-teal-500/10 border border-teal-100 dark:border-teal-500/20 rounded-2xl p-4 text-sm text-teal-700 dark:text-teal-300 leading-relaxed">
        <span className="font-bold">💡 </span>
        {dir === "rtl"
          ? "اضغط ابدأ ثم ضع هاتفك في جيبك. نحاول إبقاء الشاشة مضاءة عبر قفل التنشيط، لكن هذا يعتمد على دعم المتصفح والجهاز وقد لا يعمل في الخلفية دائماً."
          : "Press Start then pocket your phone. We try to keep the screen on via wake lock, but this depends on browser and device support and may not work in the background consistently."}
      </div>
    </div>
    </>
  );
}
